// client/src/components/bank/BidManagement.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatInterestRate, formatDuration, formatCurrencyWithSymbol } from '../utils/formatUtils';
import { useResponsive } from '../../hooks/useResponsive';
import { RotateCw } from 'lucide-react';

const BidManagement = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [bids, setBids] = useState([]);
  const [filteredBids, setFilteredBids] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Helper function to determine the rate type
  const getRateType = (bid) => {
    // First check if the bid has a rateType property
    if (bid.rateType) {
      return bid.rateType;
    }
    // Otherwise check if the request has a rateType property
    else if (bid.request?.rateType) {
      return bid.request.rateType;
    }
    // Default to fixed if no rate type is found
    else {
      return 'fixed';
    }
  };
  
  useEffect(() => {
    const fetchBids = async () => {
      try {
        const res = await axios.get('/api/bids/bank');
        
        // Add debug logging - SECURITY DISABLED
        //console.log('Received bids from API:', res.data.bids);
        
        // Filter out bids with null requests
        const validBids = res.data.bids.filter(bid => bid.request !== null);
                
        setBids(validBids);
        
        // Initially filter to show only pending bids (not yet approved)
        const pending = validBids.filter(bid => 
          bid.status === 'pending'
        );
        
        setFilteredBids(pending);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching bids:');
        setError(t('common.errors.fetchBids'));
        setLoading(false);
      }
    };
    
    fetchBids();
  }, [t]);
  
  useEffect(() => {
    // Filter bids based on active tab
    if (activeTab === 'pending') {
      setFilteredBids(bids.filter(bid => 
        bid.status === 'pending'
      ));
    } else if (activeTab === 'pending-overnight') {
      setFilteredBids(bids.filter(bid => 
        bid.status === 'pending' && 
        bid.request?.duration === 'overnight'
      ));
    } else if (activeTab === 'pending-time') {
      setFilteredBids(bids.filter(bid => 
        bid.status === 'pending' && 
        bid.request?.duration !== 'overnight'
      ));
    }
  }, [activeTab, bids]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Mobile Card Component
  const MobileBidCard = ({ bid }) => {
    const rateType = getRateType(bid);
    
    return (
      <div className="mobile-record-card">
        <div className="mobile-card-header">
          <div className="mobile-card-title">
            {bid.request?.requestId ? `#${bid.request.requestId}` : 'N/A'}
          </div>
          <div className="mobile-card-status">
            <span className={`status status-${bid.status}`}>
              {t('bids.pending')}
            </span>
          </div>
        </div>
        
        <div className="mobile-card-body">
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('requests.type')}</span>
            <span className="mobile-card-value">
              <span className="status" style={bid.request?.requestType === 'hot_money'
                ? { backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' }
                : { backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' }}>
                {bid.request?.requestType === 'hot_money' ? t('requests.hotMoney') : t('requests.allocation')}
              </span>
            </span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('requests.company')}</span>
            <span className="mobile-card-value">{bid.request?.company?.name || t('bids.notAvailable')}</span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('bids.liquidityAvailable')}</span>
            <span className="mobile-card-value">
              {bid.request?.amount ? formatCurrencyWithSymbol(bid.request.amount, bid.request?.currency || bid.currency || 'EUR') : 'N/A'}
            </span>
          </div>
          
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('bids.bidAmount')}</span>
            <span className="mobile-card-value">{formatCurrencyWithSymbol(bid.amount, bid.currency || bid.request?.currency || 'EUR')}</span>
          </div>
          
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('requests.interestRateType')}</span>
            <span className="mobile-card-value">
              {rateType === 'floating' ? t('requests.floating') : t('requests.fixed')}
            </span>
          </div>
          
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('bids.interestRate')}</span>
            <span className="mobile-card-value">{formatInterestRate(bid.interestRate, rateType, bid.baseRate)}</span>
          </div>
          
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('requests.maturity')}</span>
            <span className="mobile-card-value">
              {formatDuration(bid.request?.duration, t)}
            </span>
          </div>
        </div>
        
        <div className="mobile-card-footer">
          <div className="mobile-card-date">
            {new Date(bid.createdAt).toLocaleDateString()}
          </div>
          <div className="mobile-card-action">
            {bid.status === 'pending' && (
              <Link
                to={`/bank/bids/${bid.id}`}
                className="view-btn"
              >
                {t('common.view')}
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return <div className="loading">{t('common.loadingBids')}</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  return (
    <div className="bid-management company-dashboard">
      <div className="tab-container">
        <div className="tabs">
          <div 
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => handleTabChange('pending')}
          >
            {t('bids.pending')}
          </div>
          <div 
            className={`tab ${activeTab === 'pending-overnight' ? 'active' : ''}`}
            onClick={() => handleTabChange('pending-overnight')}
          >
            {t('bids.overnight')}
          </div>
          <div 
            className={`tab ${activeTab === 'pending-time' ? 'active' : ''}`}
            onClick={() => handleTabChange('pending-time')}
          >
            {t('bids.fixedTerm')}
          </div>
        </div>
      </div>
      
      <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
        <div className="card-title">
          <RotateCw className="card-title-icon" size={20} />
          <span>{t('bids.myPendingBids')}</span>
        </div>
        
        {/* Desktop Table */}
        {!isMobile && (
          <table>
            <thead>
              <tr>
                <th>{t('requests.requestId')}</th>
                <th>{t('requests.company')}</th>
                <th>{t('bids.liquidityAvailable')}</th>
                <th>{t('bids.bidAmount')}</th>
                <th>{t('requests.interestRateType')}</th>
                <th>{t('bids.interestRate')}</th>
                <th>{t('requests.maturity')}</th>
                <th>{t('bids.submissionDate')}</th>
                <th>{t('requests.type')}</th>
                <th>{t('bids.status')}</th>
                <th>{t('common.action')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredBids.length > 0 ? (
                filteredBids.map(bid => {
                  // Get the rate type for this bid
                  const rateType = getRateType(bid);
                  
                  return (
                    <tr key={bid.id}>
                      <td>{bid.request?.requestId ? `#${bid.request.requestId}` : 'N/A'}</td>
                      <td>{bid.request?.company?.name || t('bids.notAvailable')}</td>
                      <td>{bid.request?.amount ? formatCurrencyWithSymbol(bid.request.amount, bid.request?.currency || bid.currency || 'EUR') : 'N/A'}</td>
                      <td>{formatCurrencyWithSymbol(bid.amount, bid.currency || bid.request?.currency || 'EUR')}</td>
                      <td>{rateType === 'floating' ? t('requests.floating') : t('requests.fixed')}</td>
                      <td>{formatInterestRate(bid.interestRate, rateType, bid.baseRate)}</td>
                      <td>
                         {formatDuration(bid.request?.duration, t)}
                      </td>
                      <td>{new Date(bid.createdAt).toLocaleDateString()}</td>
                      <td>
                        <span className="status" style={bid.request?.requestType === 'hot_money'
                          ? { backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80', fontSize: '10px', padding: '3px 7px', lineHeight: '1.4' }
                          : { backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9', fontSize: '10px', padding: '3px 7px', lineHeight: '1.4' }}>
                          {bid.request?.requestType === 'hot_money' ? t('requests.hotMoney') : t('requests.allocation')}
                        </span>
                      </td>
                      <td>
                        <span className={`status status-${bid.status}`} style={{ fontSize: '10px', padding: '3px 7px', lineHeight: '1.4', border: '1px solid transparent' }}>
                          {t('bids.pending')}
                        </span>
                      </td>
                      <td>
                        {bid.status === 'pending' && (
                          <Link
                            to={`/bank/bids/${bid.id}`}
                            className="view-btn"
                            style={{ fontSize: '13px' }}
                          >
                            {t('common.view')}
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="11">{t('bids.noBidsFound')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        
        {/* Mobile Cards - Solo su mobile */}
        {isMobile && (
          <div className="mobile-cards-container">
            {filteredBids.length > 0 ? (
              filteredBids.map(bid => (
                <MobileBidCard key={bid.id} bid={bid} />
              ))
            ) : (
              <div className="mobile-record-card mobile-empty-card">
                {t('bids.noBidsFound')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BidManagement;